import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Stack, Group, Title, Text, Badge, Button, Table, Select, Textarea,
  Drawer, Loader, Alert, Paper, ScrollArea, Center, ActionIcon, Tooltip,
} from '@mantine/core';
import {
  IconUserShield, IconAlertCircle, IconRefresh, IconEye, IconMail, IconPhone, IconClock,
} from '@tabler/icons-react';
import { useHttpClient } from '@actschurch/ccms-sdk';

interface DataDeletionRequestView {
  requestID: number;
  referenceCode: string;
  fullName: string;
  email: string;
  phoneNumber?: string | null;
  details?: string | null;
  statusID: number;
  statusName: string;
  statusColor: string;
  statusIcon: string;
  requestedIpAddress?: string | null;
  adminNotes?: string | null;
  processedBy?: string | null;
  processedByName?: string | null;
  requestedDateTime: number;
  modifiedDateTime: number;
}

const STATUS_OPTIONS = [
  { value: '0', label: 'Pending', color: 'orange' },
  { value: '1', label: 'In Progress', color: 'blue' },
  { value: '2', label: 'Completed', color: 'green' },
  { value: '3', label: 'Rejected', color: 'red' },
];

function statusColor(id: number): string {
  return STATUS_OPTIONS.find((s) => s.value === String(id))?.color ?? 'gray';
}

function fmtTs(v?: number): string {
  if (!v) return '-';
  const s = String(v);
  if (s.length < 12) return s;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
}

export default function DataDeletionRequestsPage() {
  const httpClient = useHttpClient();
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const [items, setItems] = useState<DataDeletionRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const [selected, setSelected] = useState<DataDeletionRequestView | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editStatus, setEditStatus] = useState<string | null>('0');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = `/users/api/data-deletion${statusFilter != null ? `?statusId=${statusFilter}` : ''}`;
      const res: any = await httpClient.get(url);
      const body = res?.data ?? res;
      setItems((body?.data ?? []) as DataDeletionRequestView[]);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load requests.');
    } finally {
      setLoading(false);
    }
  }, [httpClient, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = useCallback((item: DataDeletionRequestView) => {
    setSelected(item);
    setEditStatus(String(item.statusID));
    setEditNotes(item.adminNotes ?? '');
    setSaveError('');
    setDrawerOpen(true);
  }, []);

  // Deep link from the admin alert email: /admin/data-deletion-requests/:id
  useEffect(() => {
    if (!params.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await httpClient.get(`/users/api/data-deletion/${params.id}`);
        const body = res?.data ?? res;
        const item = (body?.data ?? null) as DataDeletionRequestView | null;
        if (item && !cancelled) openDetail(item);
      } catch {
        /* ignore — list still shows */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params.id, httpClient, openDetail]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    if (params.id) navigate('/admin/data-deletion-requests', { replace: true });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError('');
    try {
      await httpClient.put(`/users/api/data-deletion/${selected.requestID}/status`, {
        statusId: Number(editStatus),
        adminNotes: editNotes || undefined,
      });
      closeDrawer();
      await load();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to update the request.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <Stack gap={2}>
            <Group gap="xs">
              <IconUserShield size={26} />
              <Title order={2}>Data Deletion Requests</Title>
            </Group>
            <Text c="dimmed" size="sm">
              POPIA erasure requests submitted from the public website. Review each request and update
              its status — nothing is deleted automatically.
            </Text>
          </Stack>
          <Group gap="sm">
            <Select
              placeholder="All statuses"
              clearable
              w={180}
              data={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <Tooltip label="Refresh">
              <ActionIcon variant="light" size="lg" onClick={load} aria-label="Refresh">
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error}
          </Alert>
        )}

        <Paper withBorder radius="md">
          {loading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : items.length === 0 ? (
            <Center py="xl">
              <Text c="dimmed" size="sm">No requests found.</Text>
            </Center>
          ) : (
            <ScrollArea>
              <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md" miw={760}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Reference</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Requested</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {items.map((it) => (
                    <Table.Tr key={it.requestID} style={{ cursor: 'pointer' }} onClick={() => openDetail(it)}>
                      <Table.Td><Text size="sm" fw={500}>{it.referenceCode}</Text></Table.Td>
                      <Table.Td>{it.fullName}</Table.Td>
                      <Table.Td>{it.email}</Table.Td>
                      <Table.Td>
                        <Badge color={statusColor(it.statusID)} variant="light">{it.statusName}</Badge>
                      </Table.Td>
                      <Table.Td>{fmtTs(it.requestedDateTime)}</Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="subtle"
                          leftSection={<IconEye size={14} />}
                          onClick={(e) => { e.stopPropagation(); openDetail(it); }}
                        >
                          View
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Paper>
      </Stack>

      {/* Detail / action drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        position="right"
        size="md"
        title={selected ? `Request ${selected.referenceCode}` : 'Request'}
      >
        {selected && (
          <Stack gap="md">
            <Group gap="xs">
              <Badge color={statusColor(selected.statusID)} variant="light">{selected.statusName}</Badge>
            </Group>

            <Stack gap={4}>
              <Text fw={600}>{selected.fullName}</Text>
              <Group gap="xs"><IconMail size={14} /><Text size="sm">{selected.email}</Text></Group>
              {selected.phoneNumber && (
                <Group gap="xs"><IconPhone size={14} /><Text size="sm">{selected.phoneNumber}</Text></Group>
              )}
              <Group gap="xs"><IconClock size={14} /><Text size="sm" c="dimmed">Submitted {fmtTs(selected.requestedDateTime)}</Text></Group>
            </Stack>

            {selected.details && (
              <Stack gap={2}>
                <Text size="sm" fw={500}>Details from requester</Text>
                <Paper withBorder p="sm" radius="sm"><Text size="sm">{selected.details}</Text></Paper>
              </Stack>
            )}

            {selected.requestedIpAddress && (
              <Text size="xs" c="dimmed">Submitted from IP {selected.requestedIpAddress}</Text>
            )}

            <Select
              label="Status"
              data={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
              value={editStatus}
              onChange={setEditStatus}
              allowDeselect={false}
            />

            <Textarea
              label="Admin notes"
              placeholder="Record what was done to action this request (for the audit trail)."
              value={editNotes}
              onChange={(e) => setEditNotes(e.currentTarget.value)}
              autosize
              minRows={3}
              maxRows={8}
            />

            {selected.processedByName && (
              <Text size="xs" c="dimmed">Last actioned by {selected.processedByName}</Text>
            )}

            {saveError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">{saveError}</Alert>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={closeDrawer}>Cancel</Button>
              <Button onClick={save} loading={saving}>Save</Button>
            </Group>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}
